/**
 * windPalette.ts — Windy-style wind-speed color scale.
 *
 * Port of `scripts/wind-spike/lib/palette.js` (D2). The stops are an
 * original reconstruction of Windy's public semantic order (dark → blue →
 * teal → green → yellow → orange → red → magenta with increasing speed),
 * smoothly blended between anchor colors so neighboring wind levels do not
 * form hard visual bands.
 * Hex values are tunable at the live visual review (C4) — structure is not.
 *
 * Canonical unit is knots (Windy convention). Derived lookups exist for
 * m/s (×0.514444) and km/h (×1.94384); grid math elsewhere uses m/s.
 */

export const STOPS_KT = [0, 2, 4, 7, 10, 14, 19, 24, 29, 34, 40, 48, 56, 64];
export const STOPS_HEX = [
  '#0b1626', // 0   calm — near-black navy
  '#123a63', // 2   dark blue
  '#1d5f8f', // 4   blue
  '#2b8ec4', // 7   light blue
  '#3fb3a8', // 10  teal
  '#59c96b', // 14  green
  '#a8d84f', // 19  yellow-green
  '#f2d13b', // 24  yellow
  '#f59e2e', // 29  orange
  '#f0642e', // 34  deep orange
  '#e23b2e', // 40  red
  '#b31e46', // 48  dark red
  '#8f1d9e', // 56  magenta
  '#6b1d8f', // 64+ deep purple (extreme)
];

export const KT_TO_MS = 0.514444;
export const KT_TO_KMH = 1.94384;

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

export const STOP_RGB: [number, number, number][] = STOPS_HEX.map(hexToRgb);

// ── Unit conversions ──

export function ktToMs(kt: number): number {
  return kt * KT_TO_MS;
}

export function msToKt(ms: number): number {
  return ms / KT_TO_MS;
}

export function ktToKmh(kt: number): number {
  return kt * KT_TO_KMH;
}

export function kmhToKt(kmh: number): number {
  return kmh / KT_TO_KMH;
}

// ── Color lookups ──

function stopPairForKt(kt: number): { lo: number; hi: number; t: number } {
  const speed = Math.max(STOPS_KT[0], Math.min(STOPS_KT[STOPS_KT.length - 1], kt));
  for (let hi = 1; hi < STOPS_KT.length; hi++) {
    if (speed <= STOPS_KT[hi]) {
      const lo = hi - 1;
      const t = (speed - STOPS_KT[lo]) / (STOPS_KT[hi] - STOPS_KT[lo]);
      return { lo, hi, t };
    }
  }
  const last = STOPS_KT.length - 1;
  return { lo: last, hi: last, t: 0 };
}

/** Smooth RGB color for a speed in knots. */
export function rgbForKt(kt: number): [number, number, number] {
  const { lo, hi, t } = stopPairForKt(kt);
  const a = STOP_RGB[lo];
  const b = STOP_RGB[hi];
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}

/** Discrete speed stop index for a speed in knots (clamped to range). */
export function stopIndexForKt(kt: number): number {
  if (kt <= STOPS_KT[0]) return 0;
  for (let i = 1; i < STOPS_KT.length; i++) {
    if (kt < STOPS_KT[i]) return i - 1;
  }
  return STOPS_KT.length - 1;
}

/** Smooth CSS color string for a speed in knots. */
export function colorForKt(kt: number): string {
  const [r, g, b] = rgbForKt(kt);
  return `rgb(${r},${g},${b})`;
}

/** Smooth rgba() string with the given alpha (0..1). */
export function rgbaForKt(kt: number, alpha: number): string {
  const [r, g, b] = rgbForKt(kt);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Precomputed 32-bit ABGR colors (for ImageData fast-fill). */
export const STOP_ABGR: number[] = STOP_RGB.map(([r, g, b]) => {
  return (0xff << 24) | (b << 16) | (g << 8) | r;
});

/** Smooth ABGR (alpha 255) for a speed in knots. */
export function abgrForKt(kt: number): number {
  const [r, g, b] = rgbForKt(kt);
  return (0xff << 24) | (b << 16) | (g << 8) | r;
}

/**
 * Precomputed rgba() strings for per-particle trail drawing without
 * per-frame string allocation: lut[alphaStep][speedKt].
 */
export function makeRgbaLUT(alphaSteps = 8): string[][] {
  const lut: string[][] = [];
  for (let a = 0; a < alphaSteps; a++) {
    const alpha = ((a + 1) / alphaSteps).toFixed(3);
    const row: string[] = [];
    for (let i = 0; i < STOPS_KT.length; i++) {
      const [r, g, b] = STOP_RGB[i];
      row.push(`rgba(${r},${g},${b},${alpha})`);
    }
    lut.push(row);
  }
  return lut;
}
